import { createContext, useContext, useEffect, useState } from "react";
import api from "../lib/api";

const DEFAULT_GENERAL = {
  site_name: "Fx Cloud",
  tagline: "The professional way to learn to trade.",
  description: "AI-powered demo & live trading platform.",
  contact_email: "hello@fxcloud.com",
  support_email: "support@fxcloud.com",
  copyright: "© 2026 Fx Cloud. All rights reserved.",
  maintenance_mode: false,
  logo_url: "/logo.png",
  favicon_url: "/logo.png",
  footer_text:
    "Fx Cloud — simulated trading platform for education & practice. Demo results are simulated and not indicative of real returns.",
  phone: "",
  address: "",
  timezone: "UTC",
  currency: "USD",
  business_hours: "",
  social_twitter: "",
  social_telegram: "",
  social_instagram: "",
  social_linkedin: "",
};

const DEFAULT_WHATSAPP = { enabled: false, number: "", display_name: "", welcome_message: "", button_text: "Chat with us", position: "right", show_mobile: true, show_desktop: true };

const SettingsContext = createContext({ general: DEFAULT_GENERAL, slides: [], whatsapp: DEFAULT_WHATSAPP, pages: [], captcha: { enabled: false, site_key: "" }, theme: null, loaded: false });

export function SettingsProvider({ children }) {
  const [general, setGeneral] = useState(DEFAULT_GENERAL);
  const [slides, setSlides] = useState([]);
  const [whatsapp, setWhatsapp] = useState(DEFAULT_WHATSAPP);
  const [pages, setPages] = useState([]);
  const [captcha, setCaptcha] = useState({ enabled: false, site_key: "" });
  const [theme, setTheme] = useState(null);
  const [themes, setThemes] = useState([]);
  const [trading, setTrading] = useState(null);
  const [layout, setLayout] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api
      .get("/settings/public")
      .then((r) => {
        setGeneral({ ...DEFAULT_GENERAL, ...(r.data.general || {}) });
        setSlides(r.data.hero_slides || []);
        setWhatsapp({ ...DEFAULT_WHATSAPP, ...(r.data.whatsapp || {}) });
        setPages(r.data.pages || []);
        setCaptcha(r.data.captcha || { enabled: false, site_key: "" });
        if (r.data.theme) setTheme(r.data.theme);
        setThemes(r.data.themes || []);
        if (r.data.trading) setTrading(r.data.trading);
        if (r.data.layout) setLayout(r.data.layout);
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  // Poll the active theme so scheduled theme switches take effect for open sessions.
  useEffect(() => {
    const t = setInterval(() => {
      api.get("/settings/public").then((r) => {
        if (r.data.theme) setTheme(r.data.theme);
        setThemes(r.data.themes || []);
      }).catch(() => {});
    }, 120000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (general.site_name) document.title = general.site_name;
    if (general.favicon_url) {
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = general.favicon_url;
    }
  }, [general]);

  return (
    <SettingsContext.Provider value={{ general, slides, whatsapp, pages, captcha, theme, themes, trading, layout, setLayout, loaded }}>{children}</SettingsContext.Provider>
  );
}

export const useSettings = () => useContext(SettingsContext);
